/**
 * Audio Upload Service — Pre-signed PUT flow (D-036).
 *
 * Uploads happen in three steps instead of one multipart POST:
 *
 *   1. POST /audio/presign  → API mints a 15-min signed URL, returns
 *      { upload_url, object_name, bucket, expires_in }.
 *   2. PUT raw blob directly to upload_url. No Authorization header — the
 *      URL itself is the credential. The browser talks to MinIO (via the
 *      storage.brandonwie.dev CF tunnel) and bypasses the API entirely.
 *   3. POST /audio/finalize { object_name, content_type, duration_seconds }.
 *      API verifies the object exists, creates the note row, enqueues the
 *      Whisper STT Celery task, returns NoteResponse.
 *
 * This replaces the old single-step POST /audio/upload that had Cloud Run
 * proxying bytes to NAS MinIO — a path that was broken because Cloud Run
 * can't reach the NAS Docker network.
 */

import api from "./api";
import type { NoteApiResponse } from "../types/api";

/**
 * Response from POST /audio/presign. Mirrors PresignResponse in
 * apps/api/src/crucio/features/audio/router.py.
 */
interface PresignResponse {
  upload_url: string;
  object_name: string;
  bucket: string;
  expires_in: number;
}

/**
 * Upload an audio recording via the 3-step pre-signed flow.
 *
 * Error handling is granular — each step throws with a step-specific prefix
 * so the caller (useLivePipeline) can surface where the failure happened.
 *
 * @param file     - Audio blob from MediaRecorder
 * @param metadata - Optional capture metadata (currently just duration)
 * @returns Created note in PENDING status
 */
export async function uploadAudio(
  file: Blob,
  metadata?: { duration?: number },
): Promise<NoteApiResponse> {
  const filename = buildFilename(file);
  // MediaRecorder blobs come through with a codec parameter, e.g.
  // "audio/webm;codecs=opus". The API's presign endpoint strips codec
  // params before validating, so sending the full type is safe.
  const contentType = file.type || "audio/webm";

  // ─── Step 1: presign ────────────────────────────────────────────────
  let presign: PresignResponse;
  try {
    const response = await api.post<PresignResponse>("/audio/presign", {
      content_type: contentType,
      filename,
      declared_size_bytes: file.size,
    });
    presign = response.data;
  } catch (err) {
    throw wrapStepError("presign", err);
  }

  // ─── Step 2: direct PUT to MinIO ───────────────────────────────────
  // Uses native fetch (not our api wrapper) because:
  //   - the URL already carries the auth signature; no Bearer header needed
  //   - target origin is storage.brandonwie.dev, not api.brandonwie.dev
  //   - putting FormData through api.ts would add Content-Type handling we
  //     don't want — MinIO needs the exact blob MIME, not multipart
  try {
    const putResponse = await fetch(presign.upload_url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
    if (!putResponse.ok) {
      throw new Error(
        `MinIO PUT returned ${putResponse.status} ${putResponse.statusText}`,
      );
    }
  } catch (err) {
    throw wrapStepError("upload", err);
  }

  // ─── Step 3: finalize ──────────────────────────────────────────────
  try {
    const response = await api.post<NoteApiResponse>("/audio/finalize", {
      object_name: presign.object_name,
      content_type: contentType,
      duration_seconds: metadata?.duration,
    });
    return response.data;
  } catch (err) {
    throw wrapStepError("finalize", err);
  }
}

function buildFilename(file: Blob): string {
  // MediaRecorder produces webm on Chrome/Firefox, mp4 on Safari. Name the
  // file accordingly so MinIO + Whisper infer the right format later.
  const ext = file.type.includes("mp4") ? "mp4" : "webm";
  return `recording-${Date.now()}.${ext}`;
}

function wrapStepError(
  step: "presign" | "upload" | "finalize",
  err: unknown,
): Error {
  const base = err instanceof Error ? err.message : "unknown error";
  // Prefix lets the UI distinguish which step failed. useLivePipeline reads
  // err.message directly into a user-facing toast, so keep the prefix short.
  return new Error(`${step}: ${base}`);
}
